import React, { useState, useRef } from 'react'
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit, FiSave, FiCamera, FiRefreshCw } from 'react-icons/fi'

export const ProfileSection = ({ 
  user, 
  form, 
  editing, 
  avatarPreview, 
  fileInputRef,
  onInputChange, 
  onFileChange, 
  onSave, 
  onEditToggle 
}) => {
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Informations personnelles</h2>
          <p className="mt-1 text-sm text-gray-400">
            Gérez vos informations personnelles et vos coordonnées
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={onEditToggle}
            className="inline-flex items-center px-4 py-2 border border-amber-500/30 text-sm font-medium rounded-lg shadow-sm text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 transition-all duration-300"
          >
            <FiEdit className="-ml-1 mr-2 h-4 w-4" />
            Modifier
          </button>
        ) : (
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onEditToggle}
              className="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg shadow-sm text-gray-300 bg-gray-700/50 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/30 transition-all duration-300"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {saving ? (
                <>
                  <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <FiSave className="-ml-1 mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="px-6 py-5 border-b border-amber-500/20">
          <h3 className="text-lg font-semibold text-white">Profil</h3>
          <p className="mt-1 text-sm text-gray-400">
            Informations personnelles et coordonnées
          </p>
        </div>
        <div className="divide-y divide-amber-500/10">
          <div className="px-6 py-5">
            <div className="flex items-center">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-700/50 border-2 border-amber-500/30">
                <img
                  className="h-full w-full object-cover"
                  src={avatarPreview || (user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=f59e0b&color=1f2937`)}
                  alt=""
                />
              </div>
              {editing && (
                <div className="ml-6">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onFileChange}
                    className="hidden"
                    accept="image/*"
                    id="avatar-upload"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-amber-500/30 text-sm font-medium rounded-lg text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 transition-all duration-300"
                  >
                    <FiCamera className="h-4 w-4 mr-2" />
                    Changer la photo
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Nom complet</label>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  value={form.name || ''}
                  onChange={onInputChange}
                  className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                />
              ) : (
                <div className="text-white">{user.name || 'Non spécifié'}</div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Adresse email</label>
              <div className="text-white">{user.email || 'Non spécifié'}</div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Téléphone</label>
              {editing ? (
                <input
                  type="tel"
                  name="phone"
                  value={form.phone || ''}
                  onChange={onInputChange}
                  className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                  placeholder="+33 6 12 34 56 78"
                />
              ) : (
                <div className="text-white">{form.phone || 'Non spécifié'}</div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Membre depuis</label>
              <div className="text-white">
                {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-400 mb-1">Adresse</label>
              {editing ? (
                <textarea
                  name="address"
                  rows={3}
                  value={form.address || ''}
                  onChange={onInputChange}
                  className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                  placeholder="123 Rue Exemple, 75000 Paris, France"
                />
              ) : (
                <div className="text-white">{form.address || 'Non spécifiée'}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileSection
